interface VoiceService {
  speak: (text: string) => void;
  stop: () => void;
  isSpeaking: () => boolean;
  setVoiceEnabled: (enabled: boolean) => void;
  isVoiceEnabled: () => boolean;
  getAvailableVoices: () => SpeechSynthesisVoice[];
}

class VoiceServiceImpl implements VoiceService {
  private synth: SpeechSynthesis;
  private voices: SpeechSynthesisVoice[] = [];
  private voiceEnabled: boolean = true;
  private currentUtterance: SpeechSynthesisUtterance | null = null;

  constructor() {
    this.synth = window.speechSynthesis;
    this.loadVoices();
    
    // Cargar voces cuando estén disponibles (algunos navegadores las cargan de forma asíncrona)
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = () => this.loadVoices();
    }
  }

  private loadVoices() {
    this.voices = this.synth.getVoices();
    console.log('[VOICE] Voces disponibles:', this.voices.length);
    
    // Buscar voces en español femeninas
    const spanishVoices = this.voices.filter(voice => 
      voice.lang.startsWith('es') || voice.lang.includes('Spanish')
    );
    console.log('[VOICE] Voces en español:', spanishVoices.length);
    
    // Priorizar voces femeninas en español
    const femaleSpanishVoices = this.voices.filter(voice => 
      (voice.lang.startsWith('es') || voice.lang.includes('Spanish')) &&
      (voice.name.toLowerCase().includes('female') || 
       voice.name.toLowerCase().includes('mujer') ||
       voice.name.toLowerCase().includes('google español') ||
       voice.name.toLowerCase().includes('español'))
    );
    console.log('[VOICE] Voces femeninas en español:', femaleSpanishVoices.length);
  }

  speak(text: string) {
    if (!this.voiceEnabled) {
      console.log('[VOICE] Voz desactivada');
      return;
    }

    // Detener cualquier habla en curso
    this.stop();

    const utterance = new SpeechSynthesisUtterance(text);
    this.currentUtterance = utterance;

    // Configurar voz en español femenina y materna
    // Orden de preferencia: voces locales femeninas > voces de Google > cualquier español
    const preferredFemaleVoices = [
      'google español', 'español', 'monica', 'helena', 'laura', 
      'carmen', 'elena', 'isabela', 'sofia', 'valentina',
      'mujer', 'female', 'femenina', 'niña', 'maternal'
    ];

    const getVoiceScore = (voice: SpeechSynthesisVoice) => {
      let score = 0;
      const nameLower = voice.name.toLowerCase();
      const langLower = voice.lang.toLowerCase();
      
      if (langLower.startsWith('es-es') || langLower.startsWith('es-mx')) score += 30;
      if (langLower.startsWith('es')) score += 20;
      if (langLower.includes('spanish')) score += 15;
      
      for (const keyword of preferredFemaleVoices) {
        if (nameLower.includes(keyword)) {
          score += 10;
          if (['monica', 'helena', 'laura', 'carmen', 'elena', 'isabela', 'sofia'].indexOf(keyword) !== -1) {
            score += 15;
          }
        }
      }
      
      // Penalizar voces masculinas comunes
      if (nameLower.includes('carlos') || nameLower.includes('jorge') || 
          nameLower.includes('diego') || nameLower.includes('mateo') ||
          nameLower.includes('male') || nameLower.includes('hombre')) {
        score -= 50;
      }
      
      // Preferir voces locales sobre voces remotas (suelen sonar mejor)
      if (voice.localService) score += 5;
      
      return score;
    };

    const sortedVoices = [...this.voices].sort((a, b) => getVoiceScore(b) - getVoiceScore(a));
    const spanishVoice = sortedVoices.find(voice => 
      voice.lang.startsWith('es') || voice.lang.toLowerCase().includes('spanish')
    );

    if (spanishVoice) {
      utterance.voice = spanishVoice;
      console.log('[VOICE] Usando voz:', spanishVoice.name);
    } else {
      console.log('[VOICE] No se encontró voz en español, usando voz predeterminada');
    }

    // Configurar parámetros de voz para sonar más materna y menos robótica
    utterance.lang = 'es-ES';
    utterance.rate = 0.85; // Velocidad más lenta para sonar más materna
    utterance.pitch = 1.15; // Tono más alto para sonar más femenina
    utterance.volume = 0.95; // Volumen ligeramente más suave
    
    // Configurar prosodia para sonar más natural
    if ('speechSynthesis' in window) {
      // Intentar configurar parámetros adicionales si están disponibles
      utterance.rate = 0.85;
      utterance.pitch = 1.15;
    }

    // Eventos
    utterance.onstart = () => {
      console.log('[VOICE] Iniciando habla:', text);
    };

    utterance.onend = () => {
      console.log('[VOICE] Habla finalizada');
      this.currentUtterance = null;
    };

    utterance.onerror = (event) => {
      console.error('[VOICE] Error en síntesis de voz:', event.error);
      this.currentUtterance = null;
    };

    this.synth.speak(utterance);
  }

  stop() {
    if (this.synth.speaking) {
      this.synth.cancel();
      this.currentUtterance = null;
      console.log('[VOICE] Habla detenida');
    }
  }

  isSpeaking(): boolean {
    return this.synth.speaking;
  }

  setVoiceEnabled(enabled: boolean) {
    this.voiceEnabled = enabled;
    console.log('[VOICE] Voz', enabled ? 'activada' : 'desactivada');
    
    // Si se desactiva, detener cualquier habla en curso
    if (!enabled) {
      this.stop();
    }
  }

  isVoiceEnabled(): boolean {
    return this.voiceEnabled;
  }

  getAvailableVoices(): SpeechSynthesisVoice[] {
    return this.voices;
  }
}

// Crear instancia singleton
export const voiceService = new VoiceServiceImpl();
